<template>
  <div ref="mainTable" v-dragscroll="dragscroll" class="main-table">
    <slot name="sidebar" />
    <div ref="timeTable" class="time-table-overlap">
      <div class="time-table-container">
        <TimelineTimeGrid />
        <TimelineHoursContainer :start-time="workStartTime" :end-time="workEndTime" />
        <ScheduleTimelineCurrentBar v-if="currentBar" :time="currentTimeString" />
        <slot />
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { Temporal } from '@js-temporal/polyfill'
import { useTimeoutFn, useTimestamp } from '@vueuse/core'
import { dragscroll } from 'vue-dragscroll'
import { useTemporalPlainTime } from '@/src/hooks/useTemporalPlainTime'
import { TIME_LINE_HOUR_WIDTH } from '@/src/lib/constants'
import { JST_TIMEZONE } from '@/src/lib/dateTimeUtils'

export default defineComponent({
  directives: { dragscroll },
  props: {
    workStartTime: {
      type: String,
      required: true,
    },
    workEndTime: {
      type: String,
      required: true,
    },
    dragscroll: {
      type: Boolean,
      default: false,
    },
    autoScroll: {
      type: Boolean,
      default: true,
    },
    currentBar: {
      type: Boolean,
      default: false,
    },
  },
  setup(props) {
    const timeTable = ref<HTMLDivElement>()
    const mainTable = ref<HTMLDivElement>()
    const timestamp = useTimestamp({ interval: 60_000 })
    const currentTimeString = computed<string>(() => {
      return Temporal.Instant.fromEpochMilliseconds(timestamp.value)
        .toZonedDateTimeISO(JST_TIMEZONE)
        .toPlainTime()
        .toString()
    })
    const workStartTime = useTemporalPlainTime(toRef(props, 'workStartTime'))

    const { start } = useTimeoutFn((): void => {
      if (!mainTable.value) return

      const hourPos = (workStartTime.value.hour + workStartTime.value.minute / 60) * TIME_LINE_HOUR_WIDTH

      // NOTE: scrollToをサポートしていないEdge対策
      mainTable.value.scrollLeft = hourPos - TIME_LINE_HOUR_WIDTH
    }, 10)

    onMounted(() => {
      if (props.autoScroll) {
        start()
      }
    })

    return {
      timeTable,
      mainTable,
      currentTimeString,
    }
  },
})
</script>

<style scoped lang="scss">
@use '@/stylesheets/application/resources';

.main-table {
  align-items: flex-start;
  border-bottom: 1px solid resources.$border;
  display: flex;
  justify-content: space-between;
  overflow: scroll;
  -webkit-overflow-scrolling: touch;
  position: relative;
  width: 100%;

  // cursor: move
  &:active {
    cursor: grabbing;
  }
}

.time-table-overlap {
  -webkit-overflow-scrolling: touch;
  position: relative;
  width: calc(100% - 100px);

  @include resources.mobile {
    width: calc(100% - 70px);
  }
}

.time-table-container {
  width: resources.$app-timeline-hour-width * 24;
}
</style>
